import { DateTime } from "luxon";
import { isPaidPto } from "../../components/StaffTable/utils";
import { getPtoStaffingDays } from "../../utils/dateUtils";
import type { Nurse, SpecificPreference } from "../../utils/models";

/**
 * The paid time off falling on each of `days`, keyed by ISO date, in nurse then entry order.
 *
 * Each entry's staffing days are worked out once and filed under the days they hit, rather than asking every day
 * whether the entry covers it: that walk is Luxon time-zone math, and asking per day repeated it for every day of the
 * week over every entry the nurses ever had, which froze the page on each schedule change.
 */
export const buildPtoByDayISO = (
	days: DateTime[],
	nurses: Pick<Nurse, "employeeSpecificPreferences">[],
	dayStart: string,
): Map<string, SpecificPreference[]> => {
	const isoDays = days.map((day) => day.toISODate()).filter((iso): iso is string => iso !== null);
	const map = new Map<string, SpecificPreference[]>(isoDays.map((iso) => [iso, []]));
	const earliestDay = DateTime.min(...days);
	const latestDay = DateTime.max(...days);
	if (!earliestDay || !latestDay) return map;

	// A staffing day can run from the evening before its date to the morning after it, so an entry a day either side
	// of the week can still land in it; anything further out cannot, and is skipped before its walk.
	const windowStart = earliestDay.startOf("day").minus({ days: 1 });
	const windowEnd = latestDay.startOf("day").plus({ days: 2 });

	for (const nurse of nurses) {
		for (const preference of nurse.employeeSpecificPreferences) {
			if (!isPaidPto(preference)) continue;
			if (preference.end_date < windowStart || preference.start_date >= windowEnd) continue;
			for (const staffingDay of getPtoStaffingDays(preference.start_date, preference.end_date, dayStart)) {
				const iso = staffingDay.toISODate();
				if (iso) map.get(iso)?.push(preference);
			}
		}
	}
	return map;
};

/** The schedule's time off by the nurse it belongs to, in the order it first appears: the roster's shape for it. */
export const timeOffByNurseIdOf = (timeOff: SpecificPreference[]): Map<number, SpecificPreference[]> => {
	const byNurseId = new Map<number, SpecificPreference[]>();
	for (const preference of timeOff) byNurseId.set(preference.nurse_id, [...(byNurseId.get(preference.nurse_id) ?? []), preference]);
	return byNurseId;
};
